import customersService from '@/services/customers';
import suppliersService from '@/services/suppliers';
import { extractCollection } from '@/utils/dataNormalization';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: peopleText } = useDomainText('people');
const { catalog: customersText } = useDomainText('customers');
const { catalog: billingDataText } = useDomainText('billingData');

const resolvePersonOwnerContext = (vm) => {
  const customerId = Number(vm?.customerId || 0);
  if (customerId > 0) {
    return {
      type: 'customer',
      ownerId: customerId,
      service: customersService,
    };
  }

  const supplierId = Number(vm?.supplierId || 0);
  if (supplierId > 0) {
    return {
      type: 'supplier',
      ownerId: supplierId,
      service: suppliersService,
    };
  }

  return {
    type: '',
    ownerId: 0,
    service: null,
  };
};

export const personDetailMethods = {
  async openPersonCreateModal() {
    await this.ensureGeoCatalogsLoaded();
    this.personModalMode = 'create';
    this.personModalInitialData = null;
    this.isPersonModalOpen = true;
  },
  async openPersonEditModal(person) {
    if (!person?.id) {
      return;
    }

    await this.ensureGeoCatalogsLoaded();
    this.personModalMode = 'edit';
    this.personModalInitialData = { ...person };
    this.isPersonModalOpen = true;
  },
  closePersonModal() {
    this.isPersonModalOpen = false;
    this.personModalMode = 'create';
    this.personModalInitialData = null;
    this.isPersonSubmitting = false;
  },
  async handleDeleteContact(person) {
    if (!person?.id) {
      return;
    }

    const context = resolvePersonOwnerContext(this);

    if (context.type !== 'customer') {
      if (!context.ownerId || !context.service) {
        return;
      }

      const personName = String(
        person?.name
        || `${person?.first_name || ''} ${person?.last_name || ''}`
      ).trim() || peopleText.common.noName;

      const shouldDelete = window.confirm(peopleText.confirm.messageDelete(personName));
      if (!shouldDelete) {
        return;
      }

      try {
        await context.service.personsDelete(context.ownerId, Number(person.id || 0));
        this.$toast?.success(peopleText.toast.deleteSuccess);
        await this.fetchPersons();
      } catch (error) {
        const message = error?.response?.data?.message || peopleText.toast.deleteError;
        this.$toast?.error(message);
      }

      return;
    }

    this.pendingActionType = 'delete-person';
    this.pendingActionCustomer = null;
    this.pendingActionPerson = person;
    this.pendingActionContributor = null;
    this.isConfirmActionModalOpen = true;
  },
  handleDeleteContactFromModal(person) {
    if (!person?.id) {
      return;
    }

    this.closePersonModal();
    this.handleDeleteContact(person);
  },
  closeConfirmActionModal() {
    this.isConfirmActionModalOpen = false;
    this.pendingActionType = '';
    this.pendingActionCustomer = null;
    this.pendingActionPerson = null;
    this.pendingActionContributor = null;
  },
  async confirmPendingAction() {
    try {
      const context = resolvePersonOwnerContext(this);

      if (this.pendingActionType === 'archive-customer' || this.pendingActionType === 'unarchive-customer') {
        const customer = this.pendingActionCustomer;
        if (!customer?.id) {
          return;
        }

        const nextStatus = this.pendingActionType === 'archive-customer' ? 0 : 1;
        await customersService.toggleStatus(customer.id, { active: nextStatus });

        if (nextStatus === 1) {
          this.$toast?.success(customersText.detail.generalMethods.customerUnarchiveSuccess);
        } else {
          this.$toast?.success(customersText.detail.generalMethods.customerArchiveSuccess);
        }

        await this.fetchCustomerDetail();
        return;
      }

      if (this.pendingActionType === 'delete-customer') {
        const customer = this.pendingActionCustomer;
        if (!customer?.id) {
          return;
        }

        await customersService.delete(customer.id);
        this.$toast?.success(customersText.toast.deleteSuccess);
        await this.$router.push({ name: 'customers' });
        return;
      }

      if (this.pendingActionType === 'delete-person') {
        const person = this.pendingActionPerson;
        if (!person?.id) {
          return;
        }

        await context.service?.personsDelete(context.ownerId, person.id);
        this.$toast?.success(peopleText.toast.deleteSuccess);
        await this.fetchPersons();
        return;
      }

      if (this.pendingActionType === 'delete-contributor') {
        const contributor = this.pendingActionContributor;
        if (!contributor?.id) {
          return;
        }

        await customersService.contributorsDelete(this.customerId, contributor.id);
        this.$toast?.success(billingDataText.toast.deleteSuccess);
        await this.fetchContributors();
      }
    } catch (error) {
      if (this.pendingActionType === 'archive-customer' || this.pendingActionType === 'unarchive-customer') {
        const fallbackMessage = this.pendingActionType === 'archive-customer'
          ? customersText.detail.generalMethods.customerArchiveError
          : customersText.detail.generalMethods.customerUnarchiveError;
        const message = error?.response?.data?.message || fallbackMessage;
        this.$toast?.error(message);
      } else if (this.pendingActionType === 'delete-customer') {
        const message = error?.response?.data?.message || customersText.toast.actionError;
        this.$toast?.error(message);
      } else if (this.pendingActionType === 'delete-contributor') {
        const message = error?.response?.data?.message || billingDataText.toast.deleteError;
        this.$toast?.error(message);
      } else {
        const message = error?.response?.data?.message || peopleText.toast.deleteError;
        this.$toast?.error(message);
      }
    } finally {
      this.closeConfirmActionModal();
    }
  },
  async submitPerson(payload) {
    const context = resolvePersonOwnerContext(this);
    if (!context.ownerId || !context.service) {
      return;
    }

    this.isPersonSubmitting = true;

    try {
      if (this.personModalMode === 'edit' && this.personModalInitialData?.id) {
        await context.service.personsUpdate(context.ownerId, this.personModalInitialData.id, payload || {});
        this.$toast?.success(peopleText.toast.updateSuccess);
      } else {
        await context.service.personsStore(context.ownerId, payload || {});
        this.$toast?.success(peopleText.toast.createSuccess);
      }

      this.closePersonModal();
      await this.fetchPersons();
    } catch (error) {
      const message = error?.response?.data?.message || peopleText.toast.saveError;
      this.$toast?.error(message);
    } finally {
      this.isPersonSubmitting = false;
    }
  },
  async fetchPersons() {
    const context = resolvePersonOwnerContext(this);
    if (!context.ownerId || !context.service) {
      this.persons = [];
      return;
    }

    try {
      const response = await context.service.personsIndex(context.ownerId);
      this.persons = extractCollection(response);
    } catch (error) {
      this.persons = [];
    }
  },
};